import { FileSearch } from "lucide-react";

import type { ManagerDealListItem } from "@/api/client";
import {
  ManagerDealStatusBadge,
  MANAGER_DEAL_STATUS_LABEL,
} from "@/components/deals/manager/ManagerDealStatusBadge";
import { EmptyState } from "@/components/ui/empty-state";
import { SortableHeader } from "@/components/ui/sortable-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  tableRowLinkClassName,
} from "@/components/ui/table";
import { formatAudCents } from "@/lib/spif";
import { type SortValue } from "@/lib/tableSort";
import { useTableSort } from "@/lib/useTableSort";
import { cn } from "@/lib/utils";

export interface PoDealLedgerProps {
  deals: ManagerDealListItem[];
  /** Row click — opens the master-record detail drawer over the explorer. */
  onDealClick: (dealId: string) => void;
  emptyMessage?: string;
  /** Program Insights (Wave 2): prepend a Partner (channel-partner org) column
   * and surface the owner's role (PO/BDM). Off by default so the PO org-scoped
   * ledger (single org, BDM owners) is unchanged. */
  showPartnerColumn?: boolean;
  /** Review-r2: hide the Owner column on a single-member ledger (the member
   * drawer), where every row is owned by the same person so the column is
   * redundant. Off by default. */
  hideOwnerColumn?: boolean;
}

/** Sortable columns for the PO read-only ledger (CHP-417); "partner" is the
 * program-mode-only leading column. */
type PoDealSortColumn = "partner" | "deal_id" | "client" | "owner" | "value" | "status" | "expiry";

/** Module-level so the `useTableSort` memo dependency stays stable. Owner sorts
 * on the BDM name string; Status on the visible badge label. */
function poDealSortValue(d: ManagerDealListItem, column: PoDealSortColumn): SortValue {
  switch (column) {
    case "partner":
      return d.channel_partner_company;
    case "deal_id":
      return d.deal_id_display;
    case "client":
      return d.client_name;
    case "owner":
      return d.lead_bdm_name;
    case "value":
      return d.total_contract_value;
    case "status":
      return MANAGER_DEAL_STATUS_LABEL[d.deal_status];
    case "expiry":
      return d.target_close_date ? new Date(d.target_close_date) : null;
  }
}

/** Owner display with a PO/BDM role suffix when known (program mode). */
function ownerLabel(d: ManagerDealListItem, withRole: boolean): string {
  const name = d.lead_bdm_name ?? "—";
  if (!withRole || d.lead_bdm_is_po === null) return name;
  return `${name} (${d.lead_bdm_is_po ? "PO" : "BDM"})`;
}

/** Whole-dollar AUD from cents; "—" when null. Reuses the SPIF helper so the
 * money rendering stays consistent across PO surfaces (never a raw cents int). */
function formatValue(cents: number | null): string {
  return cents != null ? formatAudCents(cents) : "—";
}

/** "12 May 2026" — matches the sibling po-insights drawers' date idiom
 * (TeamMemberDrawer.formatDate). "—" when null/unparseable. */
function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * PO Deal Explorer read-only ledger (Insights & Admin Flow B/C; parity D1/D2).
 *
 * A six-column read-only view of the Program Owner's whole-organisation deal
 * feed: Deal ID, Client, Owner, Value, Status, Expiry Date. Deliberately NOT
 * the Channel-Manager grid — a PO sees no deal-adjudication control
 * ("Change status"), so there is no action column. Row click layers the
 * master-record detail (`DealDetailDrawer`) over the explorer.
 *
 * - Deal ID  = `deal_id_display` (the human-facing deal reference)
 * - Owner    = `lead_bdm_name` (the BDM who owns the deal)
 * - Value    = `total_contract_value` via `formatAudCents` (cents → AUD)
 * - Status   = the shared `ManagerDealStatusBadge`
 * - Expiry   = `target_close_date` (the deal's registration-expiry/close date)
 */
export function PoDealLedger({
  deals,
  onDealClick,
  emptyMessage,
  showPartnerColumn = false,
  hideOwnerColumn = false,
}: PoDealLedgerProps) {
  // CHP-417: client-side column sort; unsorted leaves the caller's order intact.
  const { sort, toggle, sortedRows } = useTableSort(deals, poDealSortValue);

  if (deals.length === 0) {
    return <EmptyState icon={FileSearch} title={emptyMessage ?? "No deals in this view."} />;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {showPartnerColumn ? (
            // CHP-825: "Partner" → "Channel partner" across ledgers.
            <SortableHeader column="partner" sort={sort} onToggle={toggle}>
              Channel partner
            </SortableHeader>
          ) : null}
          <SortableHeader column="deal_id" sort={sort} onToggle={toggle}>
            Deal ID
          </SortableHeader>
          <SortableHeader column="client" sort={sort} onToggle={toggle}>
            Client
          </SortableHeader>
          {hideOwnerColumn ? null : (
            // CHP-825: "Owner" → "Deal owner" across ledgers.
            <SortableHeader column="owner" sort={sort} onToggle={toggle}>
              Deal owner
            </SortableHeader>
          )}
          <SortableHeader column="value" sort={sort} onToggle={toggle} align="right">
            Value
          </SortableHeader>
          <SortableHeader column="status" sort={sort} onToggle={toggle}>
            Status
          </SortableHeader>
          <SortableHeader column="expiry" sort={sort} onToggle={toggle}>
            Expiry Date
          </SortableHeader>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedRows.map((d) => (
          <TableRow
            interactive
            key={d.id}
            data-testid={`po-deal-row-${d.id}`}
            // CHP-832/833/865: whole-row click opens the deal detail. The row's
            // own onClick is the cross-browser pointer path — the ::after overlay
            // is pointer-events-none and Safari-unsafe (CHP-305), so it must NOT
            // be relied on for the hit-area.
            onClick={() => onDealClick(d.id)}
          >
            {showPartnerColumn ? (
              <TableCell className="text-muted-foreground" data-testid={`po-deal-partner-${d.id}`}>
                {d.channel_partner_company ?? "—"}
              </TableCell>
            ) : null}
            <TableCell>
              {/* Primary control — keyboard-focusable and named; the row onClick
                  above is the cross-browser pointer path. stopPropagation so the
                  row + button don't both fire. */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDealClick(d.id);
                }}
                className={cn(
                  "font-medium text-primary underline-offset-4 hover:underline",
                  tableRowLinkClassName,
                )}
                data-testid={`po-deal-open-${d.id}`}
              >
                {d.deal_id_display ?? "—"}
              </button>
            </TableCell>
            <TableCell>{d.client_name}</TableCell>
            {hideOwnerColumn ? null : (
              <TableCell data-testid={`po-deal-owner-${d.id}`}>
                {ownerLabel(d, showPartnerColumn)}
              </TableCell>
            )}
            <TableCell className="text-right tabular-nums">
              {formatValue(d.total_contract_value)}
            </TableCell>
            <TableCell>
              <ManagerDealStatusBadge status={d.deal_status} />
            </TableCell>
            <TableCell data-testid={`po-deal-expiry-${d.id}`}>
              {formatDate(d.target_close_date)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
